import { useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
// Libs
import { motion } from 'framer-motion';

// Styles
import styles from './Dialog.module.css';

const dialogVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

export function Dialog({
  title, children, onClose, isOpen, footer, id, topRightContent,
}) {
  const dialogElement = useRef(null);

  useLayoutEffect(() => {
    const isCurrentDialogClosed = (!dialogElement.current.open || typeof dialogElement.current.open === 'undefined');

    if (isOpen && isCurrentDialogClosed) {
      dialogElement.current.showModal();
    } else if (!isOpen && dialogElement.current.open) {
      dialogElement.current.close();
    }
  }, [dialogElement, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let lastY = 0;

    const onTouchStart = (event) => {
      lastY = event.touches[0]?.clientY ?? 0;
    };

    const onTouchMove = (event) => {
      const dialog = dialogElement.current;
      const point = event.touches[0];
      if (!point) return;

      const pullingDown = point.clientY > lastY;
      lastY = point.clientY;

      if (!dialog?.contains(event.target)) {
        event.preventDefault();
        return;
      }

      const atTop = dialog.scrollTop <= 0;
      const atBottom = Math.ceil(dialog.scrollTop + dialog.clientHeight) >= dialog.scrollHeight;
      if ((pullingDown && atTop) || (!pullingDown && atBottom)) {
        event.preventDefault();
      }
    };

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [isOpen]);

  useEffect(() => {
    if (dialogElement?.current) {
      dialogElement.current.addEventListener('cancel', onClose);
    }
    return () => {
      dialogElement?.current?.removeEventListener('cancel', onClose);
    };
  }, [onClose]);

  useEffect(() => {
    const onMousedown = (event) => {
      if (dialogElement?.current) {
        const rect = dialogElement.current.getBoundingClientRect();
        const isInDialog = rect.top <= event.clientY
        && event.clientY <= rect.top + rect.height
        && rect.left <= event.clientX
        && event.clientX <= rect.left + rect.width;
        if (!isInDialog) onClose();
      }
    };

    if (dialogElement?.current) {
      dialogElement.current.addEventListener('mousedown', onMousedown);
    }
    return () => {
      dialogElement?.current?.removeEventListener('mousedown', onMousedown);
    };
  }, [dialogElement, onClose]);

  return createPortal(
    <motion.dialog
      id={id}
      className={styles.dialog}
      ref={dialogElement}
      animate={isOpen ? 'visible' : 'hidden'}
      variants={dialogVariants}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <div className={styles.header}>
        <h2>{title}</h2>
        {topRightContent}
      </div>

      <div className={styles.body}>
        {children}
      </div>

      {footer ? (
        <div className={styles.footer}>
          {footer}
        </div>
      ) : null}
    </motion.dialog>,
    document.body,
  );
}

Dialog.propTypes = {
  title: PropTypes.string,
  children: PropTypes.node,
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  footer: PropTypes.node,
  id: PropTypes.string,
  topRightContent: PropTypes.node,
};

Dialog.defaultProps = {
  title: null,
  children: null,
  onClose: () => undefined,
  isOpen: false,
  footer: null,
  id: null,
  topRightContent: null,
};

export default Dialog;
